import { mdiPlus, mdiTune } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React from 'react';
import { isUnsetConfigValue } from '../../../services/worker-configuration/worker-configuration.types';
import { WorkerStore } from '../../../stores/worker-configuration/worker.store';
import * as DS from './configuration-detail.styles';
import { OptionField } from './option-field';
import { ConfigurationRulesTable } from '../rules/configuration-rules-table';
import * as RS from '../rules/configuration-rule.styles';

interface IConfigurationAutomationDetailProps {
    interactionDisabled: boolean;
    store: WorkerStore;
}

export const ConfigurationAutomationDetail: React.FC<IConfigurationAutomationDetailProps> = observer(
    ({ interactionDisabled, store }) => {
        const worker = store.selectedAutomationWorker;
        const optionDefinitions = store.getSelectedAutomationOptionDefinitions();
        const configuredCount = optionDefinitions.filter(
            option =>
                worker != null &&
                !isUnsetConfigValue(store.getConfigValue(worker.key, option.key)),
        ).length;

        if (!worker) {
            return (
                <RS.DetailPanelShell>
                    <RS.DetailPanelBody>
                        <DS.EmptyState>Select an automation worker to view configuration.</DS.EmptyState>
                    </RS.DetailPanelBody>
                </RS.DetailPanelShell>
            );
        }

        return (
            <RS.DetailPanelShell>
                <RS.DetailPanelHeader>
                    <RS.DetailHeaderActions>
                        <div>
                            <DS.DetailHeaderRow>
                                <DS.DetailHeader>{worker.name}</DS.DetailHeader>
                                <DS.WorkerKeyChip>{worker.key}</DS.WorkerKeyChip>
                            </DS.DetailHeaderRow>
                            <DS.DetailDescription>{worker.desc}</DS.DetailDescription>
                        </div>
                    </RS.DetailHeaderActions>
                </RS.DetailPanelHeader>

                <RS.DetailPanelBody>
                    {optionDefinitions.length > 0 && (
                        <DS.AutomationOptionsBlock>
                            <DS.DetailToolbar>
                                <DS.DetailHint>
                                    <Icon path={mdiTune} size={0.7} aria-hidden={true} />
                                    <span>Settings from the workflow options API for this worker.</span>
                                </DS.DetailHint>
                                <DS.ConfiguredCount>
                                    {configuredCount} of {optionDefinitions.length} settings configured
                                </DS.ConfiguredCount>
                            </DS.DetailToolbar>
                            <DS.OptionList data-readonly={store.readOnly ? 'true' : undefined}>
                                {optionDefinitions.map(option => (
                                    <OptionField
                                        key={option.key}
                                        option={option}
                                        status={worker.key}
                                        store={store}
                                        currentValue={store.getConfigValue(worker.key, option.key)}
                                        disabled={
                                            interactionDisabled ||
                                            !store.canEditOption(worker.key, option.key)
                                        }
                                        onChange={value =>
                                            store.setOptionValue(worker.key, option.key, value)
                                        }
                                    />
                                ))}
                            </DS.OptionList>
                        </DS.AutomationOptionsBlock>
                    )}

                    {/* Keyed by worker so search / filter / grouping reset when switching workers. */}
                    <ConfigurationRulesTable
                        key={worker.id}
                        worker={worker}
                        store={store}
                        disabled={interactionDisabled}
                    />

                    {!store.readOnly && (
                        <RS.AddRuleButton
                            type="button"
                            disabled={interactionDisabled}
                            onClick={() => store.addRule()}
                        >
                            <Icon path={mdiPlus} size={0.8} aria-hidden={true} />
                            Add rule
                        </RS.AddRuleButton>
                    )}
                </RS.DetailPanelBody>
            </RS.DetailPanelShell>
        );
    },
);
